/**
 * Storefront entry point for the standard lead-time allocation.
 *
 * This file owns only two things: reading the allocation out of the page, and
 * exposing it to the other storefront scripts. Every decision about how much
 * stock is offered, and which lead time a line carries, comes from
 * `b2b-stock-allocation.js`, which the metafield push script imports as well.
 *
 * The allocation is rendered into the page by
 * `snippets/b2b-stock-allocation-config.liquid` from the shop metafield
 * `b2b.stock_allocation`. An unseeded shop resolves to the committed defaults
 * rather than to nothing: an empty allocation computes a limit of zero and
 * renders the whole catalogue unavailable, so failing to the agreed numbers is
 * the safe direction here. That is the opposite of the ladder's choice, and
 * deliberately so, for the reason written next to `DEFAULTS`.
 *
 * We expose only the quantity available for the standard lead time so buyers do
 * not see the store's full inventory position. Higher quantities can still be
 * ordered at the extended lead time.
 *
 * Loaded as a module. The bare specifier below is resolved by the import map in
 * `layout/theme.liquid`, which Liquid fills in with the versioned asset URL.
 * Relying on a relative path instead would make the browser guess at the asset
 * URL on Shopify's CDN.
 */

import {
  DEFAULTS,
  isExtendedLeadTime,
  normalizeAllocation,
} from "b2b-stock-allocation"

const readConfig = () => {
  const node = document.querySelector("[data-b2b-stock-allocation-config]")
  if (!node) {
    return DEFAULTS
  }
  try {
    return normalizeAllocation(JSON.parse(node.textContent || "{}"))
  } catch (_error) {
    // A malformed metafield must not take the catalogue offline, so this falls
    // back to the committed numbers rather than throwing. The push script is
    // where a malformed allocation is meant to be caught, loudly.
    return DEFAULTS
  }
}

const allocation = readConfig()

const root = typeof window !== "undefined" ? window : globalThis

/**
 * The bridge to the one script that cannot import a module.
 *
 * `cart.js` is a classic deferred script, so a global is the only way it can
 * reach the rule. This object therefore carries exactly what crosses that
 * bridge and nothing else. Anything else that needs the allocation should
 * import `b2b-stock-allocation` directly, which is the real interface and the
 * one the tests pin; a wider global would be a second surface to keep in step
 * with no caller asking for it.
 */
root.B2BStockCap = {
  standardLeadDays: allocation.standardLeadDays,
  extendedLeadDays: allocation.extendedLeadDays,
  isExtendedLeadTime: (text) =>
    isExtendedLeadTime(text, allocation.extendedLeadDays),
}
